import { useState } from 'react';
import { ArrowRight, Shield, Zap, Globe, Repeat } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AvailableSwapsModal from './AvailableSwapsModal';
import { SwapOffer } from '../backend';

export default function Hero() {
    const [showAvailableSwaps, setShowAvailableSwaps] = useState(false);

    const scrollToSwap = () => {
        document.getElementById('swap-section')?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleSelectOffer = (offer: SwapOffer) => {
        // Scroll to swap interface after selecting an offer
        scrollToSwap();
        // The offer will be handled by the SwapInterface component
        // through the AvailableSwapsModal's onSelectOffer callback
    };

    return (
        <>
            <section className="relative py-24 overflow-hidden min-h-[600px] md:min-h-[700px]">
                {/* Background image with gradient overlay - high-res hero image */}
                <div className="absolute inset-0">
                    <img 
                        src="/assets/generated/kawpow-raven-hero-hd-v3.jpg?v=20251124-1" 
                        alt="Kawpow Raven Hero" 
                        className="w-full h-full object-contain object-center md:object-cover"
                        srcSet="/assets/generated/kawpow-raven-hero-hd-v3.jpg?v=20251124-1 1x, /assets/generated/kawpow-raven-hero-hd-v3.jpg?v=20251124-1 2x"
                        loading="eager"
                        onError={(e) => {
                            // Fallback to original image if high-res not available
                            const target = e.target as HTMLImageElement;
                            if (target.src !== '/assets/generated/kawpow-raven-clean.dim_1024x768.png') {
                                target.src = '/assets/generated/kawpow-raven-clean.dim_1024x768.png';
                                target.srcSet = '';
                            }
                        }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-purple-900/70 via-black/75 to-black" />
                </div>
                
                {/* Animated background elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" />
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
                </div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-4xl mx-auto text-center space-y-8">
                        <h1 className="text-5xl md:text-7xl font-bold tracking-tight pt-8">
                            <span className="bg-gradient-to-r from-primary via-purple-400 to-primary bg-clip-text text-transparent">
                                Cross-Chain DEX
                            </span>
                            <br />
                            <span className="text-white">Carried by Ravens</span>
                        </h1>
                        
                        <p className="text-xl md:text-2xl text-gray-300 max-w-2xl mx-auto">
                            Swap tokens across Bitcoin, Ethereum, Ravencoin, and other blockchains. Secure and fast.
                        </p>

                        <div className="flex flex-wrap justify-center gap-4 pt-4">
                            <Button 
                                size="lg" 
                                className="gap-2 text-lg px-8 glow-purple"
                                onClick={scrollToSwap}
                            >
                                Swap Now
                                <ArrowRight className="h-5 w-5" />
                            </Button>
                            <Button 
                                size="lg" 
                                variant="outline" 
                                className="gap-2 text-lg px-8 border-purple-500/50 hover:bg-purple-500/10 text-white transition-all duration-300 hover:border-purple-400"
                                onClick={() => setShowAvailableSwaps(true)}
                            >
                                <Repeat className="h-5 w-5" />
                                View Live Swaps
                            </Button>
                        </div>

                        {/* Features */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-12">
                            <div className="p-6 rounded-lg bg-card/80 backdrop-blur-sm border border-border hover:border-primary/50 transition-colors">
                                <Shield className="h-10 w-10 text-primary mx-auto mb-4" />
                                <h3 className="text-lg font-semibold mb-2">Secure Swaps</h3>
                                <p className="text-sm text-muted-foreground">
                                    Trusted aggregators with transparent tracking
                                </p>
                            </div>
                            
                            <div className="p-6 rounded-lg bg-card/80 backdrop-blur-sm border border-border hover:border-primary/50 transition-colors">
                                <Zap className="h-10 w-10 text-primary mx-auto mb-4" />
                                <h3 className="text-lg font-semibold mb-2">Lightning Fast</h3>
                                <p className="text-sm text-muted-foreground">
                                    Cross-chain swaps in seconds
                                </p>
                            </div>
                            
                            <div className="p-6 rounded-lg bg-card/80 backdrop-blur-sm border border-border hover:border-primary/50 transition-colors">
                                <Globe className="h-10 w-10 text-primary mx-auto mb-4" />
                                <h3 className="text-lg font-semibold mb-2">Multiple Blockchains</h3>
                                <p className="text-sm text-muted-foreground">
                                    BTC, ETH, RVN, BSC, Polygon, Base & more
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <AvailableSwapsModal 
                open={showAvailableSwaps}
                onOpenChange={setShowAvailableSwaps}
                onSelectOffer={handleSelectOffer}
            />
        </>
    );
}

